import { addDays, format } from 'date-fns'
import omit from 'lodash-es/omit'

import APPLICANTS_SNAPSHOT from '../../../rental-portal-mock-data/applicants.json'
import LISTING_DETAILS_SNAPSHOT from '../../../rental-portal-mock-data/listing-details.json'
import LISTINGS_SNAPSHOT from '../../../rental-portal-mock-data/listings.json'
import PORTAL_PUBLICATIONS_SNAPSHOT from '../../../rental-portal-mock-data/portal-publications.json'
import USERS_SNAPSHOT from '../../../rental-portal-mock-data/users.json'
import {
  HTTP_STATUS_ACCEPTED,
  HTTP_STATUS_FORBIDDEN,
  HTTP_STATUS_INTERNAL_SERVER_ERROR,
  HTTP_STATUS_NOT_FOUND,
  HTTP_STATUS_OK,
  HTTP_STATUS_UNPROCESSABLE_ENTITY,
} from '../../constants/api'
import { EMPTY_LIST, EMPTY_OBJECT, EMPTY_STRING } from '../../constants/common'
import { DATE_API_DATA_FORMAT_SHORT, HOUR } from '../../constants/date'
import {
  DATE_FILTER_TYPE,
  MULTISELECT_FILTER_TYPE,
  NUMBER_RANGE_FILTER_TYPE,
  SELECT_FILTER_TYPE,
  TOGGLE_FILTER_TYPE,
} from '../../constants/filter'
import {
  RENTAL_PORTAL_LISTING_STATUS,
  RENTAL_PORTAL_LISTING_STATUSES,
} from '../../constants/rentalPortal'
import {
  RENTAL_PORTAL_MOCK_LATENCY,
  RENTAL_PORTAL_MOCK_STATE,
} from '../../constants/rentalPortalMockState'
import {
  SUBMENU_DEFAULT_VALUE,
  SUBMENU_PARAM_ARCHIVE_VALUE,
} from '../../constants/route'
import { DESC_ORDER_SYMBOL } from '../../constants/sort'
import { isApiSuccessStatus } from '../../utils/api'
import { isValueNumberType } from '../../utils/common'
import {
  getRentalPortalMockAvatarColor,
  getRentalPortalMockMediaUrl,
  toRentalPortalMockMediaUser,
} from '../../utils/rentalPortalMockMedia'
import {
  hasRentalPortalMockCreateRight,
  hasRentalPortalMockState,
} from '../../utils/rentalPortalMockState'
import { isSubArchive } from '../../utils/route'

import {
  LISTING_COLUMN_NAME,
  LISTING_MASS_ACTION,
  LISTING_PAYLOAD_FIELD,
  LISTING_PORTAL,
  LISTING_PORTAL_SYNC_STATUS,
  LISTING_READINESS_LABEL_KEY,
  LISTING_WAITING_ON,
  LISTINGS_FILTER_PARAM,
} from './RentalPortalListing.constants'
import {
  getIsListingPublicationLive,
  getListingBuildingShortAddress,
} from './RentalPortalListing.utils'
import {
  getMockListingCoverPhoto,
  getMockListingPhotoCount,
  getMockListingPhotosPatch,
} from './RentalPortalListingPhotos.mocks'

const ATTENTION_ADD_PHOTOS_LABEL_KEY = 'RentalPortal.ListingProfileAddPhotosKey'

const ATTENTION_MANAGE_PUBLISHING_LABEL_KEY =
  'RentalPortal.ListingProfileManagePublishingBtnKey'

const ATTENTION_READINESS_RENT_LABEL_KEY =
  'RentalPortal.ListingProfileReadinessRentKey'

const ATTENTION_READINESS_DESCRIPTION_LABEL_KEY =
  'RentalPortal.ListingProfileReadinessDescriptionKey'

const MOCK_LISTING_PHASE_STATE = {
  active: 'active',
  done: 'done',
  upcoming: 'upcoming',
}

const MOCK_LISTING_PHASE_BY_STATUS = {
  [RENTAL_PORTAL_LISTING_STATUS.closed]: {
    key: RENTAL_PORTAL_LISTING_STATUS.closed,
    state: MOCK_LISTING_PHASE_STATE.done,
    states: [
      MOCK_LISTING_PHASE_STATE.done,
      MOCK_LISTING_PHASE_STATE.done,
      MOCK_LISTING_PHASE_STATE.active,
    ],
    terminalReason: RENTAL_PORTAL_LISTING_STATUS.closed,
  },
  [RENTAL_PORTAL_LISTING_STATUS.deactivated]: {
    key: RENTAL_PORTAL_LISTING_STATUS.published,
    state: MOCK_LISTING_PHASE_STATE.done,
    states: [
      MOCK_LISTING_PHASE_STATE.done,
      MOCK_LISTING_PHASE_STATE.done,
      MOCK_LISTING_PHASE_STATE.upcoming,
    ],
    terminalReason: RENTAL_PORTAL_LISTING_STATUS.deactivated,
  },
  [RENTAL_PORTAL_LISTING_STATUS.draft]: {
    key: RENTAL_PORTAL_LISTING_STATUS.draft,
    state: MOCK_LISTING_PHASE_STATE.active,
    states: [
      MOCK_LISTING_PHASE_STATE.active,
      MOCK_LISTING_PHASE_STATE.upcoming,
      MOCK_LISTING_PHASE_STATE.upcoming,
    ],
    terminalReason: null,
  },
  [RENTAL_PORTAL_LISTING_STATUS.published]: {
    key: RENTAL_PORTAL_LISTING_STATUS.published,
    state: MOCK_LISTING_PHASE_STATE.active,
    states: [
      MOCK_LISTING_PHASE_STATE.done,
      MOCK_LISTING_PHASE_STATE.active,
      MOCK_LISTING_PHASE_STATE.upcoming,
    ],
    terminalReason: null,
  },
}

const ATTENTION_ACTION_KEY = {
  fixDescription: 'fix_description',
  fixPhoto: 'fix_photo',
  fixRent: 'fix_rent',
  open: 'open',
  publishPortals: 'publish_portals',
}

const MOCK_FLAT_ROOM_COUNT_DECIMAL_PLACES = 2

const WAITING_ON_LABEL_KEY = {
  [LISTING_WAITING_ON.applicant]: 'RentalPortal.ListingWaitingOnApplicantKey',
  [LISTING_WAITING_ON.colleague]: 'RentalPortal.ListingWaitingOnColleagueKey',
  [LISTING_WAITING_ON.you]: 'RentalPortal.ListingWaitingOnYouKey',
}

function getDegradedMockListingFields() {
  return [
    hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.noAttention)
      ? LISTING_PAYLOAD_FIELD.attention
      : null,
    hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.noBucketing)
      ? LISTING_PAYLOAD_FIELD.bucketing
      : null,
  ].filter(Boolean)
}

const LISTING_SNAPSHOT_BY_ID = new Map(
  LISTINGS_SNAPSHOT.map(listing => [listing.id, listing])
)

const LISTING_DETAIL_SNAPSHOT_BY_ID = new Map(
  LISTING_DETAILS_SNAPSHOT.map(detail => [detail.id, detail])
)

const MOCK_USER_BY_ID = new Map(
  USERS_SNAPSHOT.map(user => [user.id, toRentalPortalMockMediaUser(user)])
)

function toMockAttention({
  needsYou = false,
  primaryActionKey = ATTENTION_ACTION_KEY.open,
  primaryActionLabel,
  waitingOn,
}) {
  return {
    needs_you: needsYou,
    primary_action: primaryActionLabel
      ? { key: primaryActionKey, label: primaryActionLabel }
      : null,
    waiting_on: {
      label: WAITING_ON_LABEL_KEY[waitingOn] || null,
      who: waitingOn,
    },
  }
}

function toMockListingSnapshotFields(id, updated) {
  const snapshot = LISTING_SNAPSHOT_BY_ID.get(id)

  return {
    address_public: snapshot?.address_public ?? true,
    amenities: snapshot?.amenities ?? null,
    closed_at: snapshot?.closed_at ?? null,
    created: snapshot?.created ?? updated,
    deposit_amount: snapshot?.deposit_amount ?? null,
    deposit_text: snapshot?.deposit_text ?? null,
    first_published_at: snapshot?.first_published_at ?? null,
    heating_costs: snapshot?.heating_costs ?? null,
    heating_costs_included: snapshot?.heating_costs_included ?? null,
    listing_title: snapshot?.listing_title ?? null,
    other_costs: snapshot?.other_costs ?? null,
    responsible_user:
      MOCK_USER_BY_ID.get(snapshot?.responsible_user?.id) ?? null,
    service_charge: snapshot?.service_charge ?? null,
  }
}

function getMockListingCreatedBy(id) {
  return (
    MOCK_USER_BY_ID.get(
      LISTING_DETAIL_SNAPSHOT_BY_ID.get(id)?.created_by?.id
    ) ?? null
  )
}

function getMockListingRights(id) {
  if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.readOnly)) {
    return { canArchive: false, canEdit: false }
  }

  if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.reducedManager)) {
    return { canArchive: id % 2 === 1, canEdit: id % 2 === 0 }
  }

  return { canArchive: true, canEdit: true }
}

function hasMockListingPerformanceRight() {
  return !hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.reducedManager)
}

function hasMockListingApplicantsRight() {
  return !hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.noApplicantsAccess)
}

function getMockListingPermissions({ archived, id }) {
  const { canArchive, canEdit } = getMockListingRights(id)

  return {
    can_add_file: canEdit,
    can_archive: canArchive && !archived,
    can_edit: canEdit,
    can_edit_content: canEdit,
    can_edit_responsible_user: canEdit,
    can_edit_status: canEdit,
    can_publish: canEdit,
    can_restore: canArchive && archived,
    can_view: true,
    can_view_applicants: hasMockListingApplicantsRight(),
    can_view_documents: true,
    can_view_performance: hasMockListingPerformanceRight(),
    can_view_publications: true,
    can_view_updates: true,
    can_view_viewings: true,
  }
}

function toServedMockFlat(flat) {
  return {
    ...flat,
    floor: String(flat.floor),
    room_count: Number(flat.room_count).toFixed(
      MOCK_FLAT_ROOM_COUNT_DECIMAL_PLACES
    ),
  }
}

function toMockListing({
  applicantCount = 0,
  archived = false,
  attention,
  availableFrom = null,
  awaitingApproval = false,
  building,
  depositAmount,
  depositText,
  description = null,
  flat,
  id,
  rentPrice = null,
  serviceCharge,
  status,
  updated,
}) {
  const snapshotFields = toMockListingSnapshotFields(id, updated)

  return {
    ...snapshotFields,
    applicant_count: applicantCount,
    archived,
    attention,
    available_from: availableFrom,
    awaiting_approval: awaitingApproval,
    created_by: getMockListingCreatedBy(id),
    currency: 'EUR',
    deposit_amount: depositAmount ?? snapshotFields.deposit_amount,
    deposit_text: depositText ?? snapshotFields.deposit_text,
    description:
      description ?? LISTING_DETAIL_SNAPSHOT_BY_ID.get(id)?.description ?? null,
    display_name: null,
    flat: toServedMockFlat({ ...flat, building }),
    id,
    permissions: getMockListingPermissions({ archived, id }),
    portal_status_summary: null,
    rent_price: rentPrice,
    service_charge: serviceCharge ?? snapshotFields.service_charge,
    status,
    uk_id: 1,
    unit_label: `Top ${flat.number}, ${getListingBuildingShortAddress(building)}`,
    updated,
  }
}

const MUSTERSTRASSE = {
  address: { id: 501, value: 'Musterstraße 12, 1040 Wien' },
  id: 501,
}
const LINDENWEG = {
  address: { id: 502, value: 'Lindenweg 3, 1130 Wien' },
  id: 502,
}
const BEETHOVENSTRASSE = {
  address: { id: 503, value: 'Beethovenstraße 12, 1190 Wien' },
  id: 503,
}
const PARKALLEE = {
  address: { id: 504, value: 'Parkallee 17, 1090 Wien' },
  id: 504,
}

export const MOCK_PORTAL_LOGO_PATH = {
  ImmoScout24: 'media/portals/immoscout24.svg',
  Immowelt: 'media/portals/immowelt.svg',
  Kleinanzeigen: 'media/portals/kleinanzeigen.svg',
}

const MOCK_PORTAL_CONNECTIONS = [
  {
    account_label: 'is2•••@hausverwaltung-muster.at',
    connected: true,
    enabled: true,
    last_checked_at: '2026-09-05T07:10:00Z',
    needs_reauth: false,
    portal: LISTING_PORTAL.immoscout24,
  },
  {
    account_label: 'ope•••@hausverwaltung-muster.at',
    connected: true,
    enabled: true,
    last_checked_at: '2026-09-05T07:10:00Z',
    needs_reauth: false,
    portal: LISTING_PORTAL.immowelt,
  },
]

const MOCK_LISTING_PORTAL_PUBLICATIONS = Object.fromEntries(
  Object.entries(PORTAL_PUBLICATIONS_SNAPSHOT).map(
    ([listingId, publications]) => [
      listingId,
      publications.map(publication => ({
        ...publication,
        logo_url: getRentalPortalMockMediaUrl(
          MOCK_PORTAL_LOGO_PATH[publication.portal]
        ),
      })),
    ]
  )
)

const MOCK_UNSEEN_APPLICANT_COUNT_BY_LISTING = APPLICANTS_SNAPSHOT.reduce(
  (acc, applicant) =>
    applicant.unseen && !applicant.archived
      ? acc.set(applicant.listing_id, (acc.get(applicant.listing_id) ?? 0) + 1)
      : acc,
  new Map()
)

function getMockListingNewApplicantCount(listingId) {
  return MOCK_UNSEEN_APPLICANT_COUNT_BY_LISTING.get(listingId) ?? 0
}

export function setMockListingApplicantsSeen(listingId) {
  MOCK_UNSEEN_APPLICANT_COUNT_BY_LISTING.delete(Number(listingId))
}

function getMockListingPortalStatusSummary(listingId) {
  const publications = MOCK_LISTING_PORTAL_PUBLICATIONS[listingId]

  return publications?.length > 0
    ? publications.map(({ id, logo_url, portal, sync_status }) => ({
        id,
        logo_url,
        portal,
        sync_status,
      }))
    : null
}

const MOCK_LISTING_SCHEDULES = Object.fromEntries(
  LISTING_DETAILS_SNAPSHOT.map(detail => [
    detail.id,
    {
      scheduled_deactivate: detail.scheduled_deactivate ?? null,
      scheduled_go_live: detail.scheduled_go_live ?? null,
    },
  ])
)

function getMockListingSchedule(id) {
  return (
    MOCK_LISTING_SCHEDULES[id] ?? {
      scheduled_deactivate: null,
      scheduled_go_live: null,
    }
  )
}

function toMockReadinessRow({ is_done: isDone, key }) {
  return { is_done: isDone, key, label: LISTING_READINESS_LABEL_KEY[key] }
}

const ATTENTION_ACTION_RESOLVED_BY = {
  [ATTENTION_ACTION_KEY.fixDescription]: listing =>
    Boolean(listing.description),
  [ATTENTION_ACTION_KEY.fixPhoto]: listing =>
    getMockListingPhotoCount(listing.id) > 0,
  [ATTENTION_ACTION_KEY.fixRent]: listing => listing.rent_price != null,
  [ATTENTION_ACTION_KEY.open]: listing =>
    getMockListingPhotoCount(listing.id) > 0,
  [ATTENTION_ACTION_KEY.publishPortals]: listing =>
    (MOCK_LISTING_PORTAL_PUBLICATIONS[listing.id] ?? EMPTY_LIST).some(
      getIsListingPublicationLive
    ),
}

function getIsMockListingAttentionResolved(listing) {
  const { key } = listing.attention?.primary_action ?? EMPTY_OBJECT

  return Boolean(ATTENTION_ACTION_RESOLVED_BY[key]?.(listing))
}

const MOCK_LISTING_COLLEAGUE_IDS = [78, 79, 80, 81]

export function toMockSessionUser(currentUser) {
  return currentUser
    ? {
        avatar_color:
          currentUser.avatar_color ??
          getRentalPortalMockAvatarColor(currentUser.id),
        avatar_thumb_url: currentUser.avatar,
        full_name: currentUser.fullname,
        id: currentUser.id,
      }
    : null
}

export function getMockWaitingOnColleague(seed) {
  return (
    MOCK_USER_BY_ID.get(
      MOCK_LISTING_COLLEAGUE_IDS[seed % MOCK_LISTING_COLLEAGUE_IDS.length]
    ) ?? null
  )
}

function getIsMockApplicantIdentityMasked(applicant) {
  return applicant.identity?.masked !== false || !applicant.identity?.value
}

export function toMockWaitingOnApplicant(applicant) {
  const isMasked = !applicant || getIsMockApplicantIdentityMasked(applicant)

  return applicant
    ? {
        avatar_color: getRentalPortalMockAvatarColor(applicant.id),
        full_name: isMasked ? applicant.reference : applicant.identity.value,
        id: applicant.id,
        is_identity_masked: isMasked,
      }
    : null
}

function getMockListingWaitingApplicant(id) {
  const applicants = APPLICANTS_SNAPSHOT.filter(
    item => item.listing_id === id && !item.archived
  )

  return toMockWaitingOnApplicant(
    applicants.find(
      item => item.attention?.waiting_on?.who === LISTING_WAITING_ON.applicant
    ) ?? applicants[0]
  )
}

function getMockWaitingOnUser({ currentUser, id, who }) {
  switch (who) {
    case LISTING_WAITING_ON.you:
      return toMockSessionUser(currentUser)
    case LISTING_WAITING_ON.colleague:
      return getMockWaitingOnColleague(id)
    case LISTING_WAITING_ON.applicant:
      return getMockListingWaitingApplicant(id)
    default:
      return null
  }
}

function toServedMockWaitingOnUser(user) {
  return user
    ? {
        avatar_color: user.avatar_color,
        avatar_url: user.avatar_thumb_url ?? null,
        id: user.id,
        is_identity_masked: Boolean(user.is_identity_masked),
        name: user.full_name,
      }
    : null
}

function toServedMockWaitingOn(listing, waitingOn, currentUser) {
  return {
    ...waitingOn,
    user: toServedMockWaitingOnUser(
      getMockWaitingOnUser({
        currentUser,
        id: listing.id,
        who: waitingOn.who,
      })
    ),
  }
}

function toServedMockAttention(listing, currentUser) {
  const { attention } = listing

  return getIsMockListingAttentionResolved(listing)
    ? {
        ...attention,
        needs_you: false,
        primary_action: null,
        waiting_on: { label: null, user: null, who: LISTING_WAITING_ON.nobody },
      }
    : {
        ...attention,
        waiting_on: toServedMockWaitingOn(
          listing,
          attention?.waiting_on ?? EMPTY_OBJECT,
          currentUser
        ),
      }
}

function getMockListingPublishReadiness(listing) {
  return [
    { is_done: listing.rent_price != null, key: 'rent' },
    { is_done: listing.description != null, key: 'description' },
    { is_done: getMockListingPhotoCount(listing.id) > 0, key: 'photo' },
  ].map(toMockReadinessRow)
}

function toMockListingPhaseFields({ phase, phases, status }) {
  const { key, state, states, terminalReason } =
    MOCK_LISTING_PHASE_BY_STATUS[status]

  const statusPhases = phases.map((item, index) => ({
    ...item,
    state: states[index],
  }))

  const phaseIndex = statusPhases.findIndex(item => item.key === key)

  return {
    phase: {
      ...phase,
      index: phaseIndex + 1,
      is_terminal: Boolean(terminalReason),
      key,
      label: statusPhases[phaseIndex]?.label,
      state,
      terminal_reason: terminalReason,
    },
    phases: statusPhases,
  }
}

function getMockListingPhaseFields({ listing, snapshot }) {
  return snapshot && snapshot.status !== listing.status
    ? toMockListingPhaseFields({
        phase: snapshot.phase,
        phases: snapshot.phases,
        status: listing.status,
      })
    : { phase: snapshot?.phase ?? null, phases: snapshot?.phases ?? EMPTY_LIST }
}

function toMockListingDetail(listing) {
  const snapshot = LISTING_DETAIL_SNAPSHOT_BY_ID.get(listing.id)

  return {
    ...listing,
    attention: {
      ...toServedMockAttention(listing),
      tabs_awaiting_you: snapshot?.attention?.tabs_awaiting_you ?? EMPTY_LIST,
    },
    counts: snapshot?.counts ?? {
      applicants: listing.applicant_count,
      documents: 0,
      emails: 0,
      publishings: 0,
      viewings: 0,
    },
    created_by: MOCK_USER_BY_ID.get(snapshot?.created_by?.id) ?? null,
    energy_certificate: snapshot?.energy_certificate ?? null,
    flat_archived: snapshot?.flat_archived ?? false,
    has_been_published:
      snapshot?.has_been_published ?? Boolean(listing.first_published_at),
    performance: snapshot?.performance ?? null,
    ...getMockListingPhaseFields({ listing, snapshot }),
    ...getMockListingPhotosPatch(listing.id),
    portal_publications:
      MOCK_LISTING_PORTAL_PUBLICATIONS[listing.id] ?? EMPTY_LIST,
    publish_readiness: getMockListingPublishReadiness(listing),
    updated_by: MOCK_USER_BY_ID.get(snapshot?.updated_by?.id) ?? null,
    ...getMockListingSchedule(listing.id),
  }
}

export function getMockListingDetail(listingId) {
  return withMockLatency(getMockReadDelay(true)).then(() => {
    if (getIsMockReadFailing(true)) {
      return Promise.reject(MOCK_LISTINGS_READ_ERROR)
    }

    const listing = getMockListingsSource().find(
      item => item.id === Number(listingId)
    )

    return listing
      ? { data: toMockListingDetail(listing) }
      : Promise.reject(MOCK_LISTING_NOT_FOUND_ERROR)
  })
}

export const MOCK_LISTINGS = [
  toMockListing({
    attention: toMockAttention({
      needsYou: true,
      primaryActionKey: ATTENTION_ACTION_KEY.fixPhoto,
      primaryActionLabel: ATTENTION_ADD_PHOTOS_LABEL_KEY,
      waitingOn: LISTING_WAITING_ON.you,
    }),
    building: MUSTERSTRASSE,
    flat: { area: 58, floor: 1, id: 702, number: '7', room_count: 2 },
    id: 1,
    rentPrice: 1080,
    status: RENTAL_PORTAL_LISTING_STATUS.draft,
    updated: '2026-08-17T10:20:00Z',
  }),
  toMockListing({
    attention: toMockAttention({ waitingOn: LISTING_WAITING_ON.colleague }),
    building: PARKALLEE,
    flat: { area: 47, floor: 3, id: 706, number: '14', room_count: 1 },
    id: 2,
    status: RENTAL_PORTAL_LISTING_STATUS.draft,
    updated: '2026-08-14T09:00:00Z',
  }),
  toMockListing({
    applicantCount: 5,
    attention: toMockAttention({
      needsYou: true,
      waitingOn: LISTING_WAITING_ON.you,
    }),
    availableFrom: '2026-10-01',
    building: MUSTERSTRASSE,
    flat: { area: 84, floor: 2, id: 701, number: '4', room_count: 3 },
    id: 3,
    rentPrice: 1450,
    status: RENTAL_PORTAL_LISTING_STATUS.published,
    updated: '2026-08-17T11:10:00Z',
  }),
  toMockListing({
    applicantCount: 3,
    attention: toMockAttention({ waitingOn: LISTING_WAITING_ON.colleague }),
    availableFrom: '2026-09-15',
    awaitingApproval: true,
    building: LINDENWEG,
    flat: { area: 62, floor: 1, id: 704, number: '9', room_count: 2 },
    id: 4,
    rentPrice: 1120,
    status: RENTAL_PORTAL_LISTING_STATUS.published,
    updated: '2026-08-16T09:05:00Z',
  }),
  toMockListing({
    applicantCount: 2,
    attention: toMockAttention({
      primaryActionKey: ATTENTION_ACTION_KEY.publishPortals,
      primaryActionLabel: ATTENTION_MANAGE_PUBLISHING_LABEL_KEY,
      waitingOn: LISTING_WAITING_ON.colleague,
    }),
    availableFrom: '2026-09-01',
    building: BEETHOVENSTRASSE,
    flat: { area: 55, floor: 4, id: 705, number: '18', room_count: 2 },
    id: 5,
    rentPrice: 990,
    status: RENTAL_PORTAL_LISTING_STATUS.published,
    updated: '2026-08-12T14:00:00Z',
  }),
  toMockListing({
    applicantCount: 1,
    attention: toMockAttention({ waitingOn: LISTING_WAITING_ON.nobody }),
    availableFrom: '2026-08-01',
    building: LINDENWEG,
    flat: { area: 51, floor: 0, id: 703, number: '2', room_count: 2 },
    id: 6,
    rentPrice: 940,
    status: RENTAL_PORTAL_LISTING_STATUS.closed,
    updated: '2026-08-05T17:40:00Z',
  }),
  toMockListing({
    attention: toMockAttention({ waitingOn: LISTING_WAITING_ON.nobody }),
    building: BEETHOVENSTRASSE,
    flat: { area: 41, floor: 3, id: 708, number: '11', room_count: 1 },
    id: 7,
    rentPrice: 780,
    status: RENTAL_PORTAL_LISTING_STATUS.deactivated,
    updated: '2026-06-20T15:30:00Z',
  }),
  toMockListing({
    archived: true,
    attention: toMockAttention({ waitingOn: LISTING_WAITING_ON.nobody }),
    building: MUSTERSTRASSE,
    flat: { area: 84, floor: 2, id: 701, number: '4', room_count: 3 },
    id: 8,
    rentPrice: 1390,
    status: RENTAL_PORTAL_LISTING_STATUS.deactivated,
    updated: '2025-11-30T10:00:00Z',
  }),
  toMockListing({
    archived: true,
    attention: toMockAttention({ waitingOn: LISTING_WAITING_ON.nobody }),
    building: PARKALLEE,
    flat: { area: 66, floor: 2, id: 711, number: '8', room_count: 3 },
    id: 9,
    status: RENTAL_PORTAL_LISTING_STATUS.draft,
    updated: '2026-05-18T08:15:00Z',
  }),
  toMockListing({
    attention: toMockAttention({
      needsYou: true,
      primaryActionKey: ATTENTION_ACTION_KEY.fixPhoto,
      primaryActionLabel: ATTENTION_ADD_PHOTOS_LABEL_KEY,
      waitingOn: LISTING_WAITING_ON.you,
    }),
    availableFrom: '2026-11-01',
    building: MUSTERSTRASSE,
    flat: { area: 76, floor: 4, id: 712, number: '15', room_count: 3 },
    id: 10,
    rentPrice: 1290,
    status: RENTAL_PORTAL_LISTING_STATUS.draft,
    updated: '2026-08-19T14:25:00Z',
  }),
  toMockListing({
    attention: toMockAttention({
      needsYou: true,
      primaryActionKey: ATTENTION_ACTION_KEY.fixDescription,
      primaryActionLabel: ATTENTION_READINESS_DESCRIPTION_LABEL_KEY,
      waitingOn: LISTING_WAITING_ON.you,
    }),
    building: LINDENWEG,
    flat: { area: 59, floor: 2, id: 713, number: '5', room_count: 2 },
    id: 11,
    rentPrice: 1050,
    status: RENTAL_PORTAL_LISTING_STATUS.draft,
    updated: '2026-08-18T08:50:00Z',
  }),
  toMockListing({
    attention: toMockAttention({
      primaryActionKey: ATTENTION_ACTION_KEY.fixRent,
      primaryActionLabel: ATTENTION_READINESS_RENT_LABEL_KEY,
      waitingOn: LISTING_WAITING_ON.colleague,
    }),
    building: BEETHOVENSTRASSE,
    flat: { area: 38, floor: 0, id: 714, number: '3', room_count: 1 },
    id: 12,
    status: RENTAL_PORTAL_LISTING_STATUS.draft,
    updated: '2026-08-13T16:40:00Z',
  }),
  toMockListing({
    attention: toMockAttention({ waitingOn: LISTING_WAITING_ON.nobody }),
    availableFrom: '2026-10-01',
    building: PARKALLEE,
    flat: { area: 103, floor: 3, id: 715, number: '6', room_count: 4 },
    id: 13,
    rentPrice: 1980,
    status: RENTAL_PORTAL_LISTING_STATUS.draft,
    updated: '2026-08-10T09:05:00Z',
  }),
  toMockListing({
    attention: toMockAttention({
      primaryActionKey: ATTENTION_ACTION_KEY.fixDescription,
      primaryActionLabel: ATTENTION_READINESS_DESCRIPTION_LABEL_KEY,
      waitingOn: LISTING_WAITING_ON.colleague,
    }),
    availableFrom: '2026-12-01',
    building: MUSTERSTRASSE,
    flat: { area: 64, floor: 5, id: 716, number: '21', room_count: 2 },
    id: 14,
    rentPrice: 1180,
    status: RENTAL_PORTAL_LISTING_STATUS.draft,
    updated: '2026-08-04T15:10:00Z',
  }),
  toMockListing({
    applicantCount: 26,
    attention: toMockAttention({
      needsYou: true,
      waitingOn: LISTING_WAITING_ON.you,
    }),
    availableFrom: '2026-10-15',
    building: MUSTERSTRASSE,
    flat: { area: 88, floor: 3, id: 717, number: '9', room_count: 3 },
    id: 15,
    rentPrice: 1560,
    status: RENTAL_PORTAL_LISTING_STATUS.published,
    updated: '2026-08-19T10:35:00Z',
  }),
  toMockListing({
    applicantCount: 2,
    attention: toMockAttention({ waitingOn: LISTING_WAITING_ON.colleague }),
    availableFrom: '2026-09-20',
    awaitingApproval: true,
    building: LINDENWEG,
    flat: { area: 66, floor: 1, id: 718, number: '12', room_count: 2 },
    id: 16,
    rentPrice: 1190,
    status: RENTAL_PORTAL_LISTING_STATUS.published,
    updated: '2026-08-18T16:05:00Z',
  }),
  toMockListing({
    applicantCount: 6,
    attention: toMockAttention({ waitingOn: LISTING_WAITING_ON.applicant }),
    availableFrom: '2026-11-01',
    building: BEETHOVENSTRASSE,
    flat: { area: 112, floor: 4, id: 719, number: '22', room_count: 4 },
    id: 17,
    rentPrice: 2280,
    status: RENTAL_PORTAL_LISTING_STATUS.published,
    updated: '2026-08-17T09:20:00Z',
  }),
  toMockListing({
    applicantCount: 3,
    attention: toMockAttention({ waitingOn: LISTING_WAITING_ON.nobody }),
    availableFrom: '2026-09-01',
    building: PARKALLEE,
    flat: { area: 34, floor: 0, id: 720, number: '3', room_count: 1 },
    id: 18,
    rentPrice: 690,
    status: RENTAL_PORTAL_LISTING_STATUS.published,
    updated: '2026-08-15T11:45:00Z',
  }),
  toMockListing({
    applicantCount: 2,
    attention: toMockAttention({
      needsYou: true,
      waitingOn: LISTING_WAITING_ON.you,
    }),
    availableFrom: '2026-12-01',
    building: MUSTERSTRASSE,
    flat: { area: 61, floor: 2, id: 721, number: '18', room_count: 2 },
    id: 19,
    rentPrice: 1130,
    status: RENTAL_PORTAL_LISTING_STATUS.published,
    updated: '2026-08-14T13:30:00Z',
  }),
  toMockListing({
    applicantCount: 9,
    attention: toMockAttention({ waitingOn: LISTING_WAITING_ON.colleague }),
    awaitingApproval: true,
    building: LINDENWEG,
    flat: { area: 79, floor: 0, id: 722, number: '7', room_count: 3 },
    id: 20,
    rentPrice: 1420,
    status: RENTAL_PORTAL_LISTING_STATUS.published,
    updated: '2026-08-13T10:15:00Z',
  }),
  toMockListing({
    applicantCount: 1,
    attention: toMockAttention({ waitingOn: LISTING_WAITING_ON.colleague }),
    availableFrom: '2026-10-01',
    building: BEETHOVENSTRASSE,
    flat: { area: 57, floor: 1, id: 723, number: '8', room_count: 2 },
    id: 21,
    rentPrice: 1020,
    status: RENTAL_PORTAL_LISTING_STATUS.published,
    updated: '2026-08-11T15:50:00Z',
  }),
  toMockListing({
    applicantCount: 5,
    attention: toMockAttention({
      needsYou: true,
      primaryActionKey: ATTENTION_ACTION_KEY.publishPortals,
      primaryActionLabel: ATTENTION_MANAGE_PUBLISHING_LABEL_KEY,
      waitingOn: LISTING_WAITING_ON.you,
    }),
    availableFrom: '2026-09-15',
    building: PARKALLEE,
    flat: { area: 84, floor: 2, id: 724, number: '11', room_count: 3 },
    id: 22,
    rentPrice: 1490,
    status: RENTAL_PORTAL_LISTING_STATUS.published,
    updated: '2026-08-10T14:40:00Z',
  }),
  toMockListing({
    applicantCount: 6,
    attention: toMockAttention({ waitingOn: LISTING_WAITING_ON.colleague }),
    availableFrom: '2027-01-01',
    awaitingApproval: true,
    building: MUSTERSTRASSE,
    flat: { area: 138, floor: 5, id: 725, number: '24', room_count: 5 },
    id: 23,
    rentPrice: 2400,
    status: RENTAL_PORTAL_LISTING_STATUS.published,
    updated: '2026-08-08T09:55:00Z',
  }),
  toMockListing({
    applicantCount: 2,
    attention: toMockAttention({ waitingOn: LISTING_WAITING_ON.nobody }),
    availableFrom: '2026-09-01',
    building: LINDENWEG,
    flat: { area: 41, floor: 3, id: 726, number: '14', room_count: 1 },
    id: 24,
    rentPrice: 780,
    status: RENTAL_PORTAL_LISTING_STATUS.published,
    updated: '2026-08-07T16:20:00Z',
  }),
  toMockListing({
    applicantCount: 3,
    attention: toMockAttention({ waitingOn: LISTING_WAITING_ON.applicant }),
    availableFrom: '2026-10-01',
    building: BEETHOVENSTRASSE,
    flat: { area: 68, floor: 0, id: 727, number: '6', room_count: 2 },
    id: 25,
    rentPrice: 1240,
    status: RENTAL_PORTAL_LISTING_STATUS.published,
    updated: '2026-08-06T12:25:00Z',
  }),
  toMockListing({
    applicantCount: 2,
    attention: toMockAttention({ waitingOn: LISTING_WAITING_ON.colleague }),
    availableFrom: '2026-11-15',
    building: PARKALLEE,
    flat: { area: 95, floor: 4, id: 728, number: '19', room_count: 3 },
    id: 26,
    rentPrice: 1680,
    status: RENTAL_PORTAL_LISTING_STATUS.published,
    updated: '2026-08-03T11:05:00Z',
  }),
  toMockListing({
    applicantCount: 4,
    attention: toMockAttention({ waitingOn: LISTING_WAITING_ON.nobody }),
    building: MUSTERSTRASSE,
    flat: { area: 58, floor: 1, id: 702, number: '7', room_count: 2 },
    id: 27,
    rentPrice: 1040,
    status: RENTAL_PORTAL_LISTING_STATUS.closed,
    updated: '2026-04-28T15:35:00Z',
  }),
  toMockListing({
    applicantCount: 5,
    attention: toMockAttention({ waitingOn: LISTING_WAITING_ON.nobody }),
    building: LINDENWEG,
    flat: { area: 63, floor: 2, id: 729, number: '16', room_count: 2 },
    id: 28,
    rentPrice: 1100,
    status: RENTAL_PORTAL_LISTING_STATUS.closed,
    updated: '2026-05-19T10:50:00Z',
  }),
  toMockListing({
    applicantCount: 3,
    attention: toMockAttention({ waitingOn: LISTING_WAITING_ON.nobody }),
    building: BEETHOVENSTRASSE,
    flat: { area: 91, floor: 3, id: 730, number: '27', room_count: 3 },
    id: 29,
    rentPrice: 1620,
    status: RENTAL_PORTAL_LISTING_STATUS.closed,
    updated: '2026-06-09T14:15:00Z',
  }),
  toMockListing({
    applicantCount: 2,
    attention: toMockAttention({ waitingOn: LISTING_WAITING_ON.nobody }),
    building: PARKALLEE,
    flat: { area: 47, floor: 3, id: 706, number: '14', room_count: 1 },
    id: 30,
    rentPrice: 720,
    status: RENTAL_PORTAL_LISTING_STATUS.deactivated,
    updated: '2026-03-14T13:20:00Z',
  }),
  toMockListing({
    applicantCount: 2,
    attention: toMockAttention({ waitingOn: LISTING_WAITING_ON.nobody }),
    building: MUSTERSTRASSE,
    flat: { area: 54, floor: 0, id: 731, number: '2', room_count: 2 },
    id: 31,
    rentPrice: 960,
    status: RENTAL_PORTAL_LISTING_STATUS.deactivated,
    updated: '2026-04-07T09:40:00Z',
  }),
  toMockListing({
    applicantCount: 4,
    attention: toMockAttention({ waitingOn: LISTING_WAITING_ON.nobody }),
    building: LINDENWEG,
    flat: { area: 118, floor: 4, id: 732, number: '20', room_count: 4 },
    id: 32,
    rentPrice: 2050,
    status: RENTAL_PORTAL_LISTING_STATUS.deactivated,
    updated: '2026-05-05T16:10:00Z',
  }),
  toMockListing({
    applicantCount: 2,
    archived: true,
    attention: toMockAttention({ waitingOn: LISTING_WAITING_ON.nobody }),
    building: BEETHOVENSTRASSE,
    flat: { area: 60, floor: 1, id: 733, number: '31', room_count: 2 },
    id: 33,
    rentPrice: 1080,
    status: RENTAL_PORTAL_LISTING_STATUS.closed,
    updated: '2025-12-05T10:30:00Z',
  }),
  toMockListing({
    applicantCount: 3,
    archived: true,
    attention: toMockAttention({ waitingOn: LISTING_WAITING_ON.nobody }),
    building: PARKALLEE,
    flat: { area: 87, floor: 2, id: 734, number: '9', room_count: 3 },
    id: 34,
    rentPrice: 1450,
    status: RENTAL_PORTAL_LISTING_STATUS.closed,
    updated: '2026-01-19T15:05:00Z',
  }),
  toMockListing({
    applicantCount: 1,
    archived: true,
    attention: toMockAttention({ waitingOn: LISTING_WAITING_ON.nobody }),
    building: LINDENWEG,
    flat: { area: 39, floor: 1, id: 735, number: '11', room_count: 1 },
    id: 35,
    rentPrice: 690,
    status: RENTAL_PORTAL_LISTING_STATUS.deactivated,
    updated: '2025-11-11T12:45:00Z',
  }),
  toMockListing({
    attention: toMockAttention({ waitingOn: LISTING_WAITING_ON.colleague }),
    availableFrom: '2026-09-15',
    building: MUSTERSTRASSE,
    description:
      'Gepflegte Wohnung in ruhiger Lage. Küche möbliert, Bad mit Fenster, Kellerabteil inklusive.',
    flat: { area: 112, floor: 4, id: 736, number: '30', room_count: 4 },
    id: 36,
    rentPrice: 1000,
    status: RENTAL_PORTAL_LISTING_STATUS.published,
    updated: '2026-07-20T11:45:00Z',
  }),
  toMockListing({
    attention: toMockAttention({ waitingOn: LISTING_WAITING_ON.colleague }),
    availableFrom: '2026-10-15',
    building: PARKALLEE,
    description:
      'Sanierte Einheit mit Balkon nach Süden. Fernwärme, Lift im Haus, Öffis in Gehweite.',
    flat: { area: 34, floor: 0, id: 737, number: '31', room_count: 1 },
    id: 37,
    rentPrice: 1040,
    status: RENTAL_PORTAL_LISTING_STATUS.published,
    updated: '2026-07-21T11:45:00Z',
  }),
  toMockListing({
    attention: toMockAttention({ waitingOn: LISTING_WAITING_ON.colleague }),
    availableFrom: '2026-11-15',
    building: LINDENWEG,
    description:
      'Altbau mit Stuck und Parkettböden. Zwei getrennt begehbare Zimmer, Abstellraum.',
    flat: { area: 57, floor: 1, id: 738, number: '32', room_count: 2 },
    id: 38,
    rentPrice: 1080,
    status: RENTAL_PORTAL_LISTING_STATUS.published,
    updated: '2026-07-22T11:45:00Z',
  }),
  toMockListing({
    attention: toMockAttention({ waitingOn: LISTING_WAITING_ON.colleague }),
    availableFrom: '2026-09-15',
    building: BEETHOVENSTRASSE,
    description:
      'Familientaugliche Wohnung nahe Grünraum. Küche mit Essplatz, Bad und separates WC.',
    flat: { area: 41, floor: 3, id: 739, number: '33', room_count: 1 },
    id: 39,
    rentPrice: 1120,
    status: RENTAL_PORTAL_LISTING_STATUS.published,
    updated: '2026-07-23T11:45:00Z',
  }),
  toMockListing({
    attention: toMockAttention({ waitingOn: LISTING_WAITING_ON.colleague }),
    availableFrom: '2026-10-15',
    building: MUSTERSTRASSE,
    description:
      'Gepflegte Wohnung in ruhiger Lage. Küche möbliert, Bad mit Fenster, Kellerabteil inklusive.',
    flat: { area: 95, floor: 4, id: 740, number: '34', room_count: 3 },
    id: 40,
    rentPrice: 1160,
    status: RENTAL_PORTAL_LISTING_STATUS.published,
    updated: '2026-07-24T11:45:00Z',
  }),
  toMockListing({
    attention: toMockAttention({ waitingOn: LISTING_WAITING_ON.colleague }),
    availableFrom: '2026-11-15',
    building: PARKALLEE,
    description:
      'Sanierte Einheit mit Balkon nach Süden. Fernwärme, Lift im Haus, Öffis in Gehweite.',
    flat: { area: 112, floor: 4, id: 741, number: '35', room_count: 4 },
    id: 41,
    rentPrice: 1200,
    status: RENTAL_PORTAL_LISTING_STATUS.published,
    updated: '2026-07-25T11:45:00Z',
  }),
  toMockListing({
    attention: toMockAttention({ waitingOn: LISTING_WAITING_ON.colleague }),
    availableFrom: '2026-09-15',
    building: LINDENWEG,
    description:
      'Altbau mit Stuck und Parkettböden. Zwei getrennt begehbare Zimmer, Abstellraum.',
    flat: { area: 34, floor: 0, id: 742, number: '36', room_count: 1 },
    id: 42,
    rentPrice: 1240,
    status: RENTAL_PORTAL_LISTING_STATUS.published,
    updated: '2026-07-26T11:45:00Z',
  }),
  toMockListing({
    attention: toMockAttention({ waitingOn: LISTING_WAITING_ON.colleague }),
    availableFrom: '2026-10-15',
    building: BEETHOVENSTRASSE,
    description:
      'Familientaugliche Wohnung nahe Grünraum. Küche mit Essplatz, Bad und separates WC.',
    flat: { area: 57, floor: 1, id: 743, number: '37', room_count: 2 },
    id: 43,
    rentPrice: 1280,
    status: RENTAL_PORTAL_LISTING_STATUS.published,
    updated: '2026-07-27T11:45:00Z',
  }),
  toMockListing({
    attention: toMockAttention({ waitingOn: LISTING_WAITING_ON.colleague }),
    availableFrom: '2026-11-15',
    building: MUSTERSTRASSE,
    description:
      'Gepflegte Wohnung in ruhiger Lage. Küche möbliert, Bad mit Fenster, Kellerabteil inklusive.',
    flat: { area: 41, floor: 3, id: 744, number: '38', room_count: 1 },
    id: 44,
    rentPrice: 1320,
    status: RENTAL_PORTAL_LISTING_STATUS.published,
    updated: '2026-07-28T11:45:00Z',
  }),
  toMockListing({
    attention: toMockAttention({ waitingOn: LISTING_WAITING_ON.colleague }),
    availableFrom: '2026-09-15',
    building: PARKALLEE,
    description:
      'Sanierte Einheit mit Balkon nach Süden. Fernwärme, Lift im Haus, Öffis in Gehweite.',
    flat: { area: 95, floor: 4, id: 745, number: '39', room_count: 3 },
    id: 45,
    rentPrice: 1360,
    status: RENTAL_PORTAL_LISTING_STATUS.published,
    updated: '2026-07-29T11:45:00Z',
  }),
  toMockListing({
    attention: toMockAttention({ waitingOn: LISTING_WAITING_ON.colleague }),
    availableFrom: '2026-10-15',
    building: LINDENWEG,
    description:
      'Altbau mit Stuck und Parkettböden. Zwei getrennt begehbare Zimmer, Abstellraum.',
    flat: { area: 112, floor: 4, id: 746, number: '40', room_count: 4 },
    id: 46,
    rentPrice: 1400,
    status: RENTAL_PORTAL_LISTING_STATUS.published,
    updated: '2026-07-20T11:45:00Z',
  }),
  toMockListing({
    attention: toMockAttention({ waitingOn: LISTING_WAITING_ON.colleague }),
    availableFrom: '2026-11-15',
    building: BEETHOVENSTRASSE,
    description:
      'Familientaugliche Wohnung nahe Grünraum. Küche mit Essplatz, Bad und separates WC.',
    flat: { area: 34, floor: 0, id: 747, number: '41', room_count: 1 },
    id: 47,
    rentPrice: 1440,
    status: RENTAL_PORTAL_LISTING_STATUS.published,
    updated: '2026-07-21T11:45:00Z',
  }),
  toMockListing({
    attention: toMockAttention({ waitingOn: LISTING_WAITING_ON.colleague }),
    availableFrom: '2026-09-15',
    building: MUSTERSTRASSE,
    description:
      'Gepflegte Wohnung in ruhiger Lage. Küche möbliert, Bad mit Fenster, Kellerabteil inklusive.',
    flat: { area: 57, floor: 1, id: 748, number: '42', room_count: 2 },
    id: 48,
    rentPrice: 1480,
    status: RENTAL_PORTAL_LISTING_STATUS.published,
    updated: '2026-07-22T11:45:00Z',
  }),
  toMockListing({
    attention: toMockAttention({ waitingOn: LISTING_WAITING_ON.colleague }),
    availableFrom: '2026-10-15',
    building: PARKALLEE,
    description:
      'Sanierte Einheit mit Balkon nach Süden. Fernwärme, Lift im Haus, Öffis in Gehweite.',
    flat: { area: 41, floor: 3, id: 749, number: '43', room_count: 1 },
    id: 49,
    rentPrice: 1520,
    status: RENTAL_PORTAL_LISTING_STATUS.published,
    updated: '2026-07-23T11:45:00Z',
  }),
]

export const MOCK_LISTINGS_PERMISSIONS = {
  can_create: hasRentalPortalMockCreateRight(),
  can_export: false,
}

const MOCK_LISTINGS_EMPTY = []

const MOCK_LISTINGS_BULK_SIZE = 48

const MOCK_LISTINGS_BULK_FIRST_ID = 1000

const MOCK_LISTINGS_BULK_UNSEEN_APPLICANT_COUNT = 143

const MOCK_LISTINGS_BULK_FIRST_UPDATED = Date.UTC(2026, 7, 18, 12)

const MOCK_LISTINGS_BULK_FIRST_AVAILABLE_FROM = new Date(Date.UTC(2026, 8, 1))

const MOCK_LISTINGS_BULK_BUILDINGS = [
  MUSTERSTRASSE,
  LINDENWEG,
  BEETHOVENSTRASSE,
  PARKALLEE,
]

const MOCK_LISTINGS_BULK_WAITING_ON = [
  LISTING_WAITING_ON.you,
  LISTING_WAITING_ON.colleague,
  LISTING_WAITING_ON.applicant,
  LISTING_WAITING_ON.nobody,
]

const MOCK_LISTINGS_BULK_LONG_BUILDING = {
  address: {
    id: 505,
    value:
      'Erzherzog-Johann-Prinz-Eugen-Promenade 128b, Stiege 4, 1230 Wien-Liesing',
  },
  id: 505,
}

function toMockBulkListing(index) {
  const waitingOn =
    MOCK_LISTINGS_BULK_WAITING_ON[index % MOCK_LISTINGS_BULK_WAITING_ON.length]

  return toMockListing({
    applicantCount: index === 0 ? MOCK_LISTINGS_BULK_UNSEEN_APPLICANT_COUNT : 0,
    attention: toMockAttention({
      needsYou: waitingOn === LISTING_WAITING_ON.you,
      waitingOn,
    }),
    availableFrom: format(
      addDays(MOCK_LISTINGS_BULK_FIRST_AVAILABLE_FROM, index),
      DATE_API_DATA_FORMAT_SHORT
    ),
    building:
      index === 0
        ? MOCK_LISTINGS_BULK_LONG_BUILDING
        : MOCK_LISTINGS_BULK_BUILDINGS[
            index % MOCK_LISTINGS_BULK_BUILDINGS.length
          ],
    flat: {
      area: 40 + (index % 45),
      floor: index % 5,
      id: MOCK_LISTINGS_BULK_FIRST_ID + index,
      number: `${100 + index}`,
      room_count: 1 + (index % 4),
    },
    id: MOCK_LISTINGS_BULK_FIRST_ID + index,
    rentPrice: 700 + index * 25,
    status:
      index % 6 === 5
        ? RENTAL_PORTAL_LISTING_STATUS.draft
        : RENTAL_PORTAL_LISTING_STATUS.published,
    updated: new Date(
      MOCK_LISTINGS_BULK_FIRST_UPDATED - index * HOUR
    ).toISOString(),
  })
}

let mockBulkListings = null

function getMockBulkListings() {
  if (!mockBulkListings) {
    mockBulkListings = [
      ...MOCK_LISTINGS,
      ...Array.from({ length: MOCK_LISTINGS_BULK_SIZE }, (_, index) =>
        toMockBulkListing(index)
      ),
    ]

    MOCK_UNSEEN_APPLICANT_COUNT_BY_LISTING.set(
      MOCK_LISTINGS_BULK_FIRST_ID,
      MOCK_LISTINGS_BULK_UNSEEN_APPLICANT_COUNT
    )
  }

  return mockBulkListings
}

let mockMixedEligibilityListings = null

function getMockMixedEligibilityListings() {
  mockMixedEligibilityListings ??= MOCK_LISTINGS.map((listing, index) =>
    index % 2 === 1
      ? {
          ...listing,
          permissions: {
            ...listing.permissions,
            can_archive: false,
            can_restore: false,
          },
        }
      : listing
  )

  return mockMixedEligibilityListings
}

function getMockListingsSource() {
  if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.empty)) {
    return MOCK_LISTINGS_EMPTY
  }

  if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.bulk)) {
    return getMockBulkListings()
  }

  if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.massActionMixed)) {
    return getMockMixedEligibilityListings()
  }

  return MOCK_LISTINGS
}

function toMockFilterOptions(entries) {
  return [...new Map(entries).entries()]
    .map(([value, label]) => ({ label, value }))
    .sort((left, right) => left.label.localeCompare(right.label))
}

const MOCK_LISTING_BUILDING_OPTIONS = toMockFilterOptions(
  LISTINGS_SNAPSHOT.map(listing => [
    listing.flat.building.id,
    getListingBuildingShortAddress(listing.flat.building),
  ])
)

function toMockUserFilterOptions(userIds) {
  return [...new Set(userIds)]
    .map(id => MOCK_USER_BY_ID.get(id))
    .filter(Boolean)
    .map(user => ({
      label: {
        avatar_color: user.avatar_color,
        company_post_name: user.job_title,
        email: user.email,
        fullname: user.full_name,
        id: user.id,
      },
      value: user.id,
    }))
    .sort((left, right) =>
      left.label.fullname.localeCompare(right.label.fullname)
    )
}

const MOCK_LISTING_MANAGER_OPTIONS = toMockUserFilterOptions(
  USERS_SNAPSHOT.filter(user => !user.is_archived).map(user => user.id)
)

const MOCK_LISTING_CREATED_BY_OPTIONS = toMockUserFilterOptions(
  LISTING_DETAILS_SNAPSHOT.filter(detail => detail.created_by).map(
    detail => detail.created_by.id
  )
)

const MOCK_LISTING_PORTAL_OPTIONS = toMockFilterOptions(
  MOCK_PORTAL_CONNECTIONS.map(connection => [
    connection.portal,
    connection.portal,
  ])
)

export const MOCK_LISTINGS_AVAILABLE_FILTERS = [
  {
    name: 'needs_my_action',
    query_param: LISTINGS_FILTER_PARAM.needsMyAction,
    title: 'RentalPortal.ListingNeedsMyActionFilterKey',
    type: TOGGLE_FILTER_TYPE,
  },
  {
    name: 'status',
    // TODO: drop `options` once the backend serves this facet
    options: [
      RENTAL_PORTAL_LISTING_STATUS.draft,
      RENTAL_PORTAL_LISTING_STATUS.published,
      RENTAL_PORTAL_LISTING_STATUS.closed,
      RENTAL_PORTAL_LISTING_STATUS.deactivated,
    ].map(status => ({
      label_key: RENTAL_PORTAL_LISTING_STATUSES[status].text,
      value: status,
    })),
    placeholder: 'RentalPortal.ListingStatusFilterPlaceholderKey',
    query_param: LISTINGS_FILTER_PARAM.status,
    title: 'RentalPortal.ListingStatusColumnKey',
    type: SELECT_FILTER_TYPE,
  },
  {
    name: 'rent',
    placeholder: [
      'RentalPortal.ListingRentMinPlaceholderKey',
      'RentalPortal.ListingRentMaxPlaceholderKey',
    ],
    query_param: [LISTINGS_FILTER_PARAM.rentGte, LISTINGS_FILTER_PARAM.rentLte],
    title: 'RentalPortal.ListingRentColumnKey',
    type: NUMBER_RANGE_FILTER_TYPE,
  },
  {
    name: 'available_from',
    placeholder: 'RentalPortal.ListingAvailableFromFilterPlaceholderKey',
    query_param: [
      LISTINGS_FILTER_PARAM.availableFromGte,
      LISTINGS_FILTER_PARAM.availableFromLte,
    ],
    title: 'RentalPortal.ListingAvailableFromColumnKey',
    type: DATE_FILTER_TYPE,
  },
  {
    name: LISTINGS_FILTER_PARAM.building,
    options: MOCK_LISTING_BUILDING_OPTIONS,
    placeholder: 'RentalPortal.ListingBuildingFilterPlaceholderKey',
    query_param: LISTINGS_FILTER_PARAM.building,
    title: 'RentalPortal.ListingBuildingColumnKey',
    type: SELECT_FILTER_TYPE,
  },
  {
    name: LISTINGS_FILTER_PARAM.portal,
    options: MOCK_LISTING_PORTAL_OPTIONS,
    placeholder: 'RentalPortal.ListingPortalFilterPlaceholderKey',
    query_param: LISTINGS_FILTER_PARAM.portal,
    title: 'RentalPortal.ListingPortalFilterKey',
    type: MULTISELECT_FILTER_TYPE,
  },
  {
    name: LISTINGS_FILTER_PARAM.manager,
    options: MOCK_LISTING_MANAGER_OPTIONS,
    placeholder: 'RentalPortal.ListingProfileResponsibleManagerPlaceholderKey',
    query_param: LISTINGS_FILTER_PARAM.manager,
    title: 'RentalPortal.ListingProfileResponsibleManagerKey',
    type: SELECT_FILTER_TYPE,
  },
  {
    name: LISTINGS_FILTER_PARAM.createdBy,
    options: MOCK_LISTING_CREATED_BY_OPTIONS,
    placeholder: 'RentalPortal.ListingCreatedByFilterPlaceholderKey',
    query_param: LISTINGS_FILTER_PARAM.createdBy,
    title: 'RentalPortal.ListingCreatedByColumnKey',
    type: SELECT_FILTER_TYPE,
  },
  {
    name: 'created',
    placeholder: 'RentalPortal.ListingCreatedFilterPlaceholderKey',
    query_param: [
      LISTINGS_FILTER_PARAM.createdGte,
      LISTINGS_FILTER_PARAM.createdLte,
    ],
    title: 'RentalPortal.ListingCreatedColumnKey',
    type: DATE_FILTER_TYPE,
  },
]

function withMockLatency(delay) {
  return new Promise(resolve => {
    setTimeout(resolve, delay)
  })
}

const MOCK_LISTINGS_READ_LATENCY = 300

function getMockReadDelay(isInitial) {
  const state = isInitial
    ? RENTAL_PORTAL_MOCK_STATE.loading
    : RENTAL_PORTAL_MOCK_STATE.slowRefresh

  return hasRentalPortalMockState(state)
    ? RENTAL_PORTAL_MOCK_LATENCY
    : MOCK_LISTINGS_READ_LATENCY
}

function getIsMockReadFailing(isInitial) {
  return (
    hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.error) ||
    (!isInitial &&
      hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.refreshError))
  )
}

const MOCK_LISTINGS_READ_ERROR = { response: { status: 500 } }

function getListingSortValue(listing, field) {
  switch (field) {
    case LISTING_COLUMN_NAME.Building:
      return getListingBuildingShortAddress(listing.flat?.building)
    case LISTING_COLUMN_NAME.CreatedBy:
      return listing.created_by?.full_name
    case LISTING_COLUMN_NAME.WaitingOn:
      return listing.attention?.waiting_on?.who
    default:
      return listing[field]
  }
}

function compareValues(a, b) {
  if (a == null && b == null) {
    return 0
  }

  if (a == null) {
    return -1
  }

  if (b == null) {
    return 1
  }

  if (isValueNumberType(a) && isValueNumberType(b)) {
    return a - b
  }

  return String(a).localeCompare(String(b))
}

const MOCK_LISTINGS_DEFAULT_ORDERING = [
  `${DESC_ORDER_SYMBOL}${LISTING_COLUMN_NAME.Created}`,
]

function getSortedListings(listings, ordering) {
  const orderingList =
    ordering?.length > 0 ? ordering : MOCK_LISTINGS_DEFAULT_ORDERING

  return [...listings].sort((left, right) => {
    for (const entry of orderingList) {
      const isDesc = entry.startsWith(DESC_ORDER_SYMBOL)
      const field = isDesc ? entry.slice(DESC_ORDER_SYMBOL.length) : entry

      const result = compareValues(
        getListingSortValue(left, field),
        getListingSortValue(right, field)
      )

      if (result !== 0) {
        return isDesc ? -result : result
      }
    }

    return 0
  })
}

function getListingsForSubView(listings, sub) {
  const isArchive = isSubArchive(sub)

  return listings.filter(listing => Boolean(listing.archived) === isArchive)
}

function getMockListingDay(value) {
  return value?.slice(0, DATE_API_DATA_FORMAT_SHORT.length) ?? null
}

const MOCK_LISTING_FACET_MATCH = {
  [LISTINGS_FILTER_PARAM.building]: (listing, value) =>
    String(listing.flat?.building?.id) === String(value),
  [LISTINGS_FILTER_PARAM.createdBy]: (listing, value) =>
    String(listing.created_by?.id) === String(value),
  [LISTINGS_FILTER_PARAM.createdGte]: (listing, value) =>
    getMockListingDay(listing.created) >= value,
  [LISTINGS_FILTER_PARAM.createdLte]: (listing, value) =>
    getMockListingDay(listing.created) <= value,
  [LISTINGS_FILTER_PARAM.manager]: (listing, value) =>
    String(listing.responsible_user?.id) === String(value),
  [LISTINGS_FILTER_PARAM.portal]: (listing, values) =>
    (MOCK_LISTING_PORTAL_PUBLICATIONS[listing.id] ?? EMPTY_LIST).some(
      publication =>
        [values].flat().includes(publication.portal) &&
        getIsListingPublicationLive(publication)
    ),
}

function hasMockListingFacetMatch(listing, filterValues) {
  return Object.entries(filterValues ?? EMPTY_OBJECT).every(
    ([param, value]) =>
      !value ||
      !MOCK_LISTING_FACET_MATCH[param] ||
      MOCK_LISTING_FACET_MATCH[param](listing, value)
  )
}

function getFilteredListings(listings, filterValues) {
  const status = filterValues?.[LISTINGS_FILTER_PARAM.status]
  const rentGte = filterValues?.[LISTINGS_FILTER_PARAM.rentGte]
  const rentLte = filterValues?.[LISTINGS_FILTER_PARAM.rentLte]

  const availableFromGte =
    filterValues?.[LISTINGS_FILTER_PARAM.availableFromGte]

  const availableFromLte =
    filterValues?.[LISTINGS_FILTER_PARAM.availableFromLte]

  const needsMyAction = filterValues?.[LISTINGS_FILTER_PARAM.needsMyAction]

  return listings.filter(
    listing =>
      hasMockListingFacetMatch(listing, filterValues) &&
      (!needsMyAction || listing.attention?.needs_you === true) &&
      (!status || listing.status === status) &&
      (!rentGte ||
        (listing.rent_price != null &&
          listing.rent_price >= Number(rentGte))) &&
      (!rentLte ||
        (listing.rent_price != null &&
          listing.rent_price <= Number(rentLte))) &&
      (!availableFromGte ||
        (listing.available_from != null &&
          listing.available_from >= availableFromGte)) &&
      (!availableFromLte ||
        (listing.available_from != null &&
          listing.available_from <= availableFromLte))
  )
}

function getPagedListings(listings, { page, pageSize }) {
  const start = (page - 1) * pageSize

  return listings.slice(start, start + pageSize)
}

function toServedMockListing(listing, currentUser) {
  return omit(
    {
      ...listing,
      applicants: { new_count: getMockListingNewApplicantCount(listing.id) },
      attention: toServedMockAttention(listing, currentUser),
      cover_photo: getMockListingCoverPhoto(listing.id),
      portal_status_summary: getMockListingPortalStatusSummary(listing.id),
      publish_readiness: getMockListingPublishReadiness(listing),
    },
    getDegradedMockListingFields()
  )
}

export function getMockListingsPage({
  currentUser,
  filterValues,
  isInitial,
  ordering,
  page,
  pageSize,
  sub,
}) {
  return withMockLatency(getMockReadDelay(isInitial)).then(() => {
    if (getIsMockReadFailing(isInitial)) {
      return Promise.reject(MOCK_LISTINGS_READ_ERROR)
    }

    const allItems = getSortedListings(
      getFilteredListings(
        getListingsForSubView(getMockListingsSource(), sub),
        filterValues
      ),
      ordering
    ).map(listing => toServedMockListing(listing, currentUser))

    const count = allItems.length
    const pageCount = Math.ceil(count / pageSize)
    const safePage = Math.min(page, Math.max(1, pageCount))

    return {
      allItems,
      items: {
        items: getPagedListings(allItems, { page: safePage, pageSize }),
        permissions: MOCK_LISTINGS_PERMISSIONS,
      },
      pagination: { count, page: safePage, pageCount, pageSize },
    }
  })
}

export const MOCK_LISTABLE_FLATS = [
  {
    area: 63,
    building: BEETHOVENSTRASSE,
    floor: 1,
    id: 707,
    number: '5',
    room_count: 2,
  },
  {
    area: 78,
    building: PARKALLEE,
    floor: 4,
    id: 709,
    number: '21',
    room_count: 3,
  },
  {
    area: 52,
    building: MUSTERSTRASSE,
    floor: 0,
    id: 710,
    number: '1',
    room_count: 2,
  },
  {
    area: 58,
    building: MUSTERSTRASSE,
    floor: 1,
    id: 750,
    number: '5',
    room_count: 2,
  },
  {
    area: 84,
    building: MUSTERSTRASSE,
    floor: 2,
    id: 751,
    number: '10',
    room_count: 3,
  },
  {
    area: 38,
    building: MUSTERSTRASSE,
    floor: 3,
    id: 752,
    number: '12',
    room_count: 1,
  },
  {
    area: 61,
    building: LINDENWEG,
    floor: 0,
    id: 753,
    number: '1',
    room_count: 2,
  },
  {
    area: 77,
    building: LINDENWEG,
    floor: 1,
    id: 754,
    number: '3',
    room_count: 3,
  },
  {
    area: 103,
    building: LINDENWEG,
    floor: 2,
    id: 755,
    number: '6',
    room_count: 4,
  },
  {
    area: 34,
    building: BEETHOVENSTRASSE,
    floor: 0,
    id: 756,
    number: '2',
    room_count: 1,
  },
  {
    area: 66,
    building: BEETHOVENSTRASSE,
    floor: 1,
    id: 757,
    number: '4',
    room_count: 2,
  },
  {
    area: 91,
    building: BEETHOVENSTRASSE,
    floor: 2,
    id: 758,
    number: '9',
    room_count: 3,
  },
  {
    area: 55,
    building: PARKALLEE,
    floor: 0,
    id: 759,
    number: '2',
    room_count: 2,
  },
  {
    area: 72,
    building: PARKALLEE,
    floor: 1,
    id: 760,
    number: '5',
    room_count: 3,
  },
  {
    area: 118,
    building: PARKALLEE,
    floor: 3,
    id: 761,
    number: '10',
    room_count: 4,
  },
].map(toServedMockFlat)

function hasMockListableFlatSearchMatch(flat, search) {
  const value = search.trim().toLowerCase()

  return `${flat.number} ${flat.building.address.value}`
    .toLowerCase()
    .includes(value)
}

function hasMockListableFlatMatch(flat, { building, search }) {
  return (
    (!building || String(flat.building.id) === String(building)) &&
    (!search || hasMockListableFlatSearchMatch(flat, search))
  )
}

export function getMockListableFlats({ building, search }) {
  return withMockLatency(getMockReadDelay(true)).then(() => {
    if (getIsMockReadFailing(true)) {
      return Promise.reject(MOCK_LISTINGS_READ_ERROR)
    }

    const items = hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.noUnits)
      ? EMPTY_LIST
      : MOCK_LISTABLE_FLATS.filter(flat =>
          hasMockListableFlatMatch(flat, { building, search })
        )

    return {
      data: {
        items,
        meta: { count: items.length, curr_page: 1, page_count: 1 },
      },
    }
  })
}

const MOCK_LISTABLE_BUILDING_OPTIONS = [
  ...new Map(
    MOCK_LISTABLE_FLATS.map(flat => [
      flat.building.id,
      {
        label: { title: getListingBuildingShortAddress(flat.building) },
        value: flat.building.id,
      },
    ])
  ).values(),
].sort((left, right) => left.label.title.localeCompare(right.label.title))

function hasMockBuildingOptionSearchMatch(option, search) {
  return option.label.title.toLowerCase().includes(search.trim().toLowerCase())
}

export function getMockListingBuildingOptions({ search }) {
  return withMockLatency(getMockReadDelay(true)).then(() => {
    if (getIsMockReadFailing(true)) {
      return Promise.reject(MOCK_LISTINGS_READ_ERROR)
    }

    const items = hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.empty)
      ? EMPTY_LIST
      : MOCK_LISTABLE_BUILDING_OPTIONS.filter(
          option => !search || hasMockBuildingOptionSearchMatch(option, search)
        )

    return {
      data: {
        items,
        meta: { count: items.length, curr_page: 1, page_count: 1 },
      },
    }
  })
}

const MOCK_MANAGER_OPTIONS = USERS_SNAPSHOT.filter(user => !user.is_archived)
  .map(user => ({
    label: {
      archived: user.is_archived,
      avatar_color: user.avatar_color,
      company_post_name: user.job_title,
      email: user.email,
      fullname: user.full_name,
    },
    value: user.id,
  }))
  .sort((a, b) => a.label.fullname.localeCompare(b.label.fullname))

function hasMockManagerOptionSearchMatch(option, search) {
  const value = search.trim().toLowerCase()

  return `${option.label.fullname} ${option.label.email}`
    .toLowerCase()
    .includes(value)
}

export function getMockManagerOptions({ search }) {
  return withMockLatency(getMockReadDelay(true)).then(() => {
    if (getIsMockReadFailing(true)) {
      return Promise.reject(MOCK_LISTINGS_READ_ERROR)
    }

    const items = hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.empty)
      ? EMPTY_LIST
      : MOCK_MANAGER_OPTIONS.filter(
          option => !search || hasMockManagerOptionSearchMatch(option, search)
        )

    return {
      data: {
        items,
        meta: { count: items.length, curr_page: 1, page_count: 1 },
      },
    }
  })
}

let nextMockListingId = 100

const MOCK_LISTING_WRITE_ERROR = { response: { status: 500 } }

const MOCK_LISTING_NOT_FOUND_ERROR = {
  response: { status: HTTP_STATUS_NOT_FOUND },
}

function getMockWriteDelay() {
  return hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.saveError) ||
    hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.loading)
    ? RENTAL_PORTAL_MOCK_LATENCY
    : 0
}

function getMockListingCreateAttention({ description, rentPrice }) {
  const [primaryActionKey, primaryActionLabel] = !rentPrice
    ? [ATTENTION_ACTION_KEY.fixRent, ATTENTION_READINESS_RENT_LABEL_KEY]
    : !description
      ? [
          ATTENTION_ACTION_KEY.fixDescription,
          ATTENTION_READINESS_DESCRIPTION_LABEL_KEY,
        ]
      : [ATTENTION_ACTION_KEY.fixPhoto, ATTENTION_ADD_PHOTOS_LABEL_KEY]

  return toMockAttention({
    needsYou: true,
    primaryActionKey,
    primaryActionLabel,
    waitingOn: LISTING_WAITING_ON.you,
  })
}

export function addMockListing({
  available_from: availableFrom,
  deposit_amount: depositAmount,
  deposit_text: depositText,
  description,
  flat: flatId,
  rent_price: rentPrice,
  service_charge: serviceCharge,
}) {
  return withMockLatency(getMockWriteDelay()).then(() => {
    if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.saveError)) {
      return Promise.reject(MOCK_LISTING_WRITE_ERROR)
    }

    const flat = MOCK_LISTABLE_FLATS.find(item => item.id === Number(flatId))

    if (!flat) {
      return Promise.reject(MOCK_LISTING_NOT_FOUND_ERROR)
    }

    const listing = toMockListing({
      attention: getMockListingCreateAttention({ description, rentPrice }),
      availableFrom: availableFrom || null,
      building: flat.building,
      depositAmount: depositAmount ?? null,
      depositText: depositText?.trim() ? depositText : null,
      description: description || null,
      flat,
      id: nextMockListingId,
      rentPrice: rentPrice || null,
      serviceCharge: serviceCharge ?? null,
      status: RENTAL_PORTAL_LISTING_STATUS.draft,
      updated: new Date().toISOString(),
    })

    nextMockListingId += 1

    getMockListingsSource().unshift(listing)

    return { data: listing }
  })
}

function replaceMockListing(id, getNextListing) {
  const source = getMockListingsSource()

  const index = source.findIndex(item => item.id === id)

  if (index !== -1) {
    source[index] = {
      ...getNextListing(source[index]),
      updated: new Date().toISOString(),
    }
  }
}

export function setMockListingArchived(id, archived) {
  replaceMockListing(id, listing => ({
    ...listing,
    archived,
    permissions: getMockListingPermissions({ archived, id: listing.id }),
  }))

  if (archived) {
    MOCK_LISTING_PORTAL_PUBLICATIONS[id]?.forEach(publication => {
      if (getIsListingPublicationLive(publication)) {
        cancelMockPublicationDispatch({
          listingId: id,
          portal: publication.portal,
        })

        publication.sync_status = LISTING_PORTAL_SYNC_STATUS.deactivated
        publication.public_url = null
        publication.scheduled_publish_at = null
      }
    })
  }
}

const MOCK_LISTING_RESPONSIBLE_USER_FIELD = 'responsible_user'

const MOCK_LISTING_PATCH_FIELDS = {
  [MOCK_LISTING_RESPONSIBLE_USER_FIELD]: value => value ?? null,
  available_from: value => value || null,
  deposit_amount: value => value ?? null,
  deposit_text: value => (value?.trim() ? value : null),
  description: value => (value?.trim() ? value : null),
  display_name: value => (value?.trim() ? value : null),
  heating_costs: value => value ?? null,
  heating_costs_included: value => Boolean(value),
  other_costs: value => value ?? null,
  rent_price: value => value ?? null,
  service_charge: value => value ?? null,
  status: value => value,
}

function getMockListingPatch(payload) {
  const patch = {}

  Object.entries(MOCK_LISTING_PATCH_FIELDS).forEach(([field, toFieldValue]) => {
    if (field in payload) {
      patch[field] = toFieldValue(payload[field])
    }
  })

  return MOCK_LISTING_RESPONSIBLE_USER_FIELD in patch
    ? {
        ...patch,
        [MOCK_LISTING_RESPONSIBLE_USER_FIELD]:
          MOCK_USER_BY_ID.get(patch[MOCK_LISTING_RESPONSIBLE_USER_FIELD]) ??
          null,
      }
    : patch
}

function getMockListingUnitPatch({ listing, payload }) {
  return payload.unit ? { flat: { ...listing.flat, ...payload.unit } } : null
}

function getMockListingFlatPatch({ listing, payload }) {
  const flat = MOCK_LISTABLE_FLATS.find(
    item => item.id === Number(payload.flat)
  )

  return flat && flat.id !== listing.flat.id
    ? {
        flat,
        unit_label: `Top ${flat.number}, ${getListingBuildingShortAddress(flat.building)}`,
      }
    : null
}

function getMockListingUnassignedAttentionPatch({ listing, patch }) {
  return MOCK_LISTING_RESPONSIBLE_USER_FIELD in patch &&
    patch[MOCK_LISTING_RESPONSIBLE_USER_FIELD] === null
    ? {
        attention: {
          ...listing.attention,
          needs_you: false,
          waiting_on: { label: null, who: LISTING_WAITING_ON.nobody },
        },
      }
    : null
}

const MOCK_LISTING_PUBLISH_BLOCKED_ERROR_KEY =
  'ServerError.RentalListingPublishBlockedKey'

function getMockListingPublishBlockedError(missing) {
  return {
    response: {
      data: {
        errors: { status: [MOCK_LISTING_PUBLISH_BLOCKED_ERROR_KEY] },
        missing,
      },
      status: HTTP_STATUS_UNPROCESSABLE_ENTITY,
    },
  }
}

function setMockListingSchedule(id, schedule) {
  MOCK_LISTING_SCHEDULES[id] = { ...getMockListingSchedule(id), ...schedule }

  replaceMockListing(id, listing => listing)
}

function findMockListing(listingId) {
  return getMockListingsSource().find(item => item.id === Number(listingId))
}

const MOCK_LISTING_SCHEDULE_FIELDS = [
  'scheduled_deactivate',
  'scheduled_go_live',
]

function getMockListingSchedulePatch(payload) {
  const fields = MOCK_LISTING_SCHEDULE_FIELDS.filter(field => field in payload)

  return fields.length > 0
    ? Object.fromEntries(fields.map(field => [field, payload[field]]))
    : null
}

function getMockListingPublishBlockers({ listing, payload }) {
  return payload.status === RENTAL_PORTAL_LISTING_STATUS.published
    ? getMockListingPublishReadiness({
        ...listing,
        ...getMockListingPatch(payload),
      })
        .filter(check => !check.is_done)
        .map(check => check.label)
    : EMPTY_LIST
}

export function patchMockListing(listingId, payload) {
  return withMockLatency(getMockWriteDelay()).then(() => {
    if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.saveError)) {
      return Promise.reject(MOCK_LISTING_WRITE_ERROR)
    }

    const listing = findMockListing(listingId)

    if (!listing) {
      return Promise.reject(MOCK_LISTING_NOT_FOUND_ERROR)
    }

    const missing = getMockListingPublishBlockers({ listing, payload })

    if (missing.length > 0) {
      return Promise.reject(getMockListingPublishBlockedError(missing))
    }

    const schedule = getMockListingSchedulePatch(payload)

    if (schedule) {
      setMockListingSchedule(listing.id, schedule)
    }

    const patch = getMockListingPatch(payload)

    replaceMockListing(listing.id, item => ({
      ...item,
      ...patch,
      ...getMockListingUnitPatch({ listing: item, payload }),
      ...getMockListingFlatPatch({ listing: item, payload }),
      ...getMockListingUnassignedAttentionPatch({ listing: item, patch }),
      updated: new Date().toISOString(),
    }))

    return { data: findMockListing(listing.id) }
  })
}

export function getMockListingsLivePublicationCount(ids) {
  return ids.filter(id =>
    (MOCK_LISTING_PORTAL_PUBLICATIONS[id] ?? EMPTY_LIST).some(
      getIsListingPublicationLive
    )
  ).length
}

export function getMockListingLivePortalNames(listingId) {
  return (MOCK_LISTING_PORTAL_PUBLICATIONS[listingId] ?? EMPTY_LIST)
    .filter(getIsListingPublicationLive)
    .map(publication => publication.portal)
}

function getMockPortalConnections() {
  return hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.empty)
    ? MOCK_PORTAL_CONNECTIONS.map(connection => ({
        ...connection,
        account_label: null,
        connected: false,
      }))
    : MOCK_PORTAL_CONNECTIONS
}

export function getMockIntegrationsStatus() {
  return withMockLatency(getMockReadDelay(true)).then(() => {
    if (getIsMockReadFailing(true)) {
      return Promise.reject(MOCK_LISTINGS_READ_ERROR)
    }

    return { data: { portals: getMockPortalConnections() } }
  })
}

const MOCK_STATE_PUBLICATION_DISPATCH_ERROR = 'publication-dispatch-error'

const MOCK_STATE_PUBLICATION_DISPATCH_MIXED = 'publication-dispatch-mixed'

const MOCK_PUBLICATION_DISPATCH_FAILING_PORTAL = 'Immowelt'

const MOCK_PUBLICATION_DISPATCH_DELAY = 4000

const MOCK_PUBLICATION_DISPATCH_ATTEMPTS = new Map()

const MOCK_PUBLICATION_ERROR_CODE = 'portal_rejected'

const MOCK_PUBLICATION_EXTERNAL_ID_PREFIX = {
  ImmoScout24: 'IS24',
  Immowelt: 'IW',
}

const MOCK_PUBLICATION_AD_URL_PREFIX = {
  ImmoScout24: 'https://www.immobilienscout24.de/expose/mock-',
  Immowelt: 'https://www.immowelt.de/expose/mock-',
}

const MOCK_PUBLICATION_SCHEDULE_TARGET = {
  deactivate: 'deactivate',
  publish: 'publish',
}

let nextMockPublicationId = 100

function toMockPortalPublication({ deactivateAt, portal, publishAt }) {
  nextMockPublicationId += 1

  return {
    external_listing_id: null,
    id: nextMockPublicationId,
    last_action_at: new Date().toISOString(),
    last_error: null,
    logo_url: getRentalPortalMockMediaUrl(MOCK_PORTAL_LOGO_PATH[portal]),
    portal,
    public_url: null,
    published_at: null,
    scheduled_deactivate_at: deactivateAt ?? null,
    scheduled_publish_at: publishAt ?? null,
    sync_status: LISTING_PORTAL_SYNC_STATUS.queued,
  }
}

function upsertMockPortalPublication({ listingId, patch, portal }) {
  const publications = MOCK_LISTING_PORTAL_PUBLICATIONS[listingId] ?? EMPTY_LIST

  const current = publications.find(
    publication => publication.portal === portal
  )

  const next = {
    ...(current ?? toMockPortalPublication({ portal })),
    ...patch,
    last_action_at: new Date().toISOString(),
  }

  MOCK_LISTING_PORTAL_PUBLICATIONS[listingId] = current
    ? publications.map(publication =>
        publication.portal === portal ? next : publication
      )
    : [...publications, next]

  return next
}

function findMockPublicationListingId(publicationId) {
  return Object.keys(MOCK_LISTING_PORTAL_PUBLICATIONS).find(listingId =>
    MOCK_LISTING_PORTAL_PUBLICATIONS[listingId].some(
      publication => publication.id === publicationId
    )
  )
}

function findMockPublication(publicationId) {
  const listingId = findMockPublicationListingId(publicationId)

  return listingId
    ? MOCK_LISTING_PORTAL_PUBLICATIONS[listingId].find(
        publication => publication.id === publicationId
      )
    : null
}

function getMockPublicationLivePatch({ listingId, portal }) {
  const current = (
    MOCK_LISTING_PORTAL_PUBLICATIONS[listingId] ?? EMPTY_LIST
  ).find(publication => publication.portal === portal)

  const externalId =
    current?.external_listing_id ??
    `${MOCK_PUBLICATION_EXTERNAL_ID_PREFIX[portal] ?? portal}-${nextMockPublicationId}`

  return {
    external_listing_id: externalId,
    last_error: null,
    public_url: `${MOCK_PUBLICATION_AD_URL_PREFIX[portal] ?? EMPTY_STRING}${externalId}`,
    published_at:
      current?.published_at ?? format(new Date(), DATE_API_DATA_FORMAT_SHORT),
    scheduled_publish_at: null,
    sync_status: LISTING_PORTAL_SYNC_STATUS.published,
  }
}

function getMockPublicationPendingPatch(syncStatus) {
  return {
    ...(syncStatus === LISTING_PORTAL_SYNC_STATUS.queued
      ? { public_url: null }
      : null),
    last_error: null,
    scheduled_publish_at: null,
    sync_status: syncStatus,
  }
}

function getMockPublicationDispatchKey({ listingId, portal }) {
  return `${listingId}:${portal}`
}

function cancelMockPublicationDispatch({ listingId, portal }) {
  const key = getMockPublicationDispatchKey({ listingId, portal })

  MOCK_PUBLICATION_DISPATCH_ATTEMPTS.set(
    key,
    (MOCK_PUBLICATION_DISPATCH_ATTEMPTS.get(key) ?? 0) + 1
  )

  return MOCK_PUBLICATION_DISPATCH_ATTEMPTS.get(key)
}

function startMockPublicationDispatch({ listingId, portal }) {
  const key = getMockPublicationDispatchKey({ listingId, portal })

  const attempt = cancelMockPublicationDispatch({ listingId, portal })

  setTimeout(() => {
    if (MOCK_PUBLICATION_DISPATCH_ATTEMPTS.get(key) !== attempt) {
      return
    }

    upsertMockPortalPublication({
      listingId,
      patch: getMockPublicationLivePatch({ listingId, portal }),
      portal,
    })

    replaceMockListing(listingId, listing => ({
      ...listing,
      ...getMockListingPublishedPatch({
        listing,
        publications: MOCK_LISTING_PORTAL_PUBLICATIONS[listingId] ?? EMPTY_LIST,
      }),
    }))
  }, MOCK_PUBLICATION_DISPATCH_DELAY)
}

function getHasMockPublicationDispatchError(portal) {
  return (
    hasRentalPortalMockState(MOCK_STATE_PUBLICATION_DISPATCH_ERROR) ||
    (hasRentalPortalMockState(MOCK_STATE_PUBLICATION_DISPATCH_MIXED) &&
      portal === MOCK_PUBLICATION_DISPATCH_FAILING_PORTAL)
  )
}

function getMockPublicationDispatchPatch({
  portal,
  publishAt,
  syncStatus = LISTING_PORTAL_SYNC_STATUS.queued,
}) {
  if (getHasMockPublicationDispatchError(portal)) {
    return {
      last_error: { code: MOCK_PUBLICATION_ERROR_CODE, message: null },
      public_url: null,
      sync_status: LISTING_PORTAL_SYNC_STATUS.error,
    }
  }

  return publishAt
    ? {
        last_error: null,
        scheduled_publish_at: publishAt,
        sync_status: LISTING_PORTAL_SYNC_STATUS.queued,
      }
    : getMockPublicationPendingPatch(syncStatus)
}

function getMockListingPublishedPatch({ listing, publications }) {
  return publications.some(getIsListingPublicationLive)
    ? {
        first_published_at:
          listing.first_published_at ?? new Date().toISOString(),
        status:
          listing.status === RENTAL_PORTAL_LISTING_STATUS.draft
            ? RENTAL_PORTAL_LISTING_STATUS.published
            : listing.status,
      }
    : null
}

function withMockPublicationWrite(listingId, getPublications) {
  return withMockLatency(getMockWriteDelay()).then(() => {
    if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.saveError)) {
      return Promise.reject(MOCK_LISTING_WRITE_ERROR)
    }

    const listing = findMockListing(listingId)

    if (!listing) {
      return Promise.reject(MOCK_LISTING_NOT_FOUND_ERROR)
    }

    const publications = getPublications(listing)

    replaceMockListing(listing.id, item => ({
      ...item,
      ...getMockListingPublishedPatch({ listing: item, publications }),
    }))

    return { data: { publications }, status: HTTP_STATUS_ACCEPTED }
  })
}

export function publishMockListingToPortals(
  listingId,
  { deactivate_at: deactivateAt, portals, publish_at: publishAt }
) {
  console.debug('[RP mock] POST listing/%s/portals/publish/', listingId, {
    deactivate_at: deactivateAt,
    portals,
    publish_at: publishAt,
  })

  return withMockPublicationWrite(listingId, listing =>
    portals.map(portal => {
      const publications = upsertMockPortalPublication({
        listingId: listing.id,
        patch: {
          ...getMockPublicationDispatchPatch({ portal, publishAt }),
          scheduled_deactivate_at: deactivateAt ?? null,
        },
        portal,
      })

      if (!publishAt && !getHasMockPublicationDispatchError(portal)) {
        startMockPublicationDispatch({ listingId: listing.id, portal })
      }

      return publications
    })
  )
}

export function updateMockListingOnPortals(listingId, { portals }) {
  console.debug('[RP mock] POST listing/%s/portals/update/', listingId, {
    portals,
  })

  return withMockPublicationWrite(listingId, listing =>
    portals.map(portal => {
      const publications = upsertMockPortalPublication({
        listingId: listing.id,
        patch: getMockPublicationDispatchPatch({
          portal,
          syncStatus: LISTING_PORTAL_SYNC_STATUS.updating,
        }),
        portal,
      })

      if (!getHasMockPublicationDispatchError(portal)) {
        startMockPublicationDispatch({ listingId: listing.id, portal })
      }

      return publications
    })
  )
}

export function deactivateMockListingOnPortals(
  listingId,
  { deactivate_at: deactivateAt, portals }
) {
  console.debug('[RP mock] POST listing/%s/portals/deactivate/', listingId, {
    deactivate_at: deactivateAt,
    portals,
  })

  return withMockPublicationWrite(listingId, listing =>
    portals.map(portal => {
      if (!deactivateAt) {
        cancelMockPublicationDispatch({ listingId: listing.id, portal })
      }

      return upsertMockPortalPublication({
        listingId: listing.id,
        patch: deactivateAt
          ? { scheduled_deactivate_at: deactivateAt }
          : {
              last_error: null,
              public_url: null,
              scheduled_deactivate_at: null,
              sync_status: LISTING_PORTAL_SYNC_STATUS.deactivated,
            },
        portal,
      })
    })
  )
}

export function retryMockListingPublication(publicationId) {
  console.debug(
    '[RP mock] POST portal-publication/%s/retry/',
    publicationId,
    null
  )

  const { portal } = findMockPublication(publicationId) ?? EMPTY_OBJECT

  return withMockPublicationWrite(
    findMockPublicationListingId(publicationId),
    listing => {
      const publication = upsertMockPortalPublication({
        listingId: listing.id,
        patch: getMockPublicationDispatchPatch({ portal }),
        portal,
      })

      if (!getHasMockPublicationDispatchError(portal)) {
        startMockPublicationDispatch({ listingId: listing.id, portal })
      }

      return [publication]
    }
  )
}

export function cancelMockListingPublicationSchedule(
  publicationId,
  { which } = EMPTY_OBJECT
) {
  console.debug(
    '[RP mock] POST portal-publication/%s/cancel-schedule/',
    publicationId,
    { which }
  )

  const { portal } = findMockPublication(publicationId) ?? EMPTY_OBJECT

  return withMockPublicationWrite(
    findMockPublicationListingId(publicationId),
    listing => [
      upsertMockPortalPublication({
        listingId: listing.id,
        patch:
          which === MOCK_PUBLICATION_SCHEDULE_TARGET.deactivate
            ? { scheduled_deactivate_at: null }
            : {
                scheduled_publish_at: null,
                sync_status: LISTING_PORTAL_SYNC_STATUS.notPublished,
              },
        portal,
      }),
    ]
  )
}

function getMockMassActionDelay() {
  return hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.massActionError) ||
    hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.massActionPartial)
    ? RENTAL_PORTAL_MOCK_LATENCY
    : getMockWriteDelay()
}

const mockListingsMassActionRequests = []

export function getMockListingsMassActionRequests() {
  return mockListingsMassActionRequests
}

function getIsMockListingMassActionEligible(listing, action) {
  return Boolean(
    action === LISTING_MASS_ACTION.restore
      ? listing.permissions.can_restore
      : listing.permissions.can_archive
  )
}

function getMockListingsMassActionTargets({ all, filters }) {
  const {
    archived,
    exclude_id: excludeId = EMPTY_LIST,
    id: idFilter = EMPTY_LIST,
    pk: ids = idFilter,
    ...facets
  } = filters

  const scoped = getFilteredListings(
    getListingsForSubView(
      getMockListingsSource(),
      archived ? SUBMENU_PARAM_ARCHIVE_VALUE : SUBMENU_DEFAULT_VALUE
    ),
    facets
  )

  return all
    ? scoped.filter(listing => !excludeId.includes(listing.id))
    : scoped.filter(listing => ids.includes(listing.id))
}

function getMockListingsMassActionStatusCode({ action, index, listing }) {
  if (!getIsMockListingMassActionEligible(listing, action)) {
    return HTTP_STATUS_FORBIDDEN
  }

  return hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.massActionPartial) &&
    index % 2 === 1
    ? HTTP_STATUS_INTERNAL_SERVER_ERROR
    : HTTP_STATUS_OK
}

export function performMockListingsMassAction(request) {
  mockListingsMassActionRequests.push(request)

  console.debug('[RP mock] POST listings/mass-action/', request)

  return withMockLatency(getMockMassActionDelay()).then(() => {
    if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.massActionError)) {
      return Promise.reject(MOCK_LISTING_WRITE_ERROR)
    }

    const { action, all, filters } = request

    const items = getMockListingsMassActionTargets({ all, filters }).map(
      (listing, index) => ({
        id: listing.id,
        status_code: getMockListingsMassActionStatusCode({
          action,
          index,
          listing,
        }),
      })
    )

    const successIds = items
      .filter(item => isApiSuccessStatus(item.status_code))
      .map(item => item.id)

    successIds.forEach(id => {
      setMockListingArchived(id, action !== LISTING_MASS_ACTION.restore)
    })

    return {
      data: { items, success_ids: successIds },
      status: HTTP_STATUS_OK,
    }
  })
}
